/**
 * Board — kanban columns by status with HTML5 drag-and-drop.
 *
 * Filters live in the URL querystring (`useFilters`), so a board view is
 * shareable by copying the address. Dropping a card PATCHes the ticket's
 * status; columns the API would reject as an illegal transition are dimmed
 * while a drag is in flight, and the move is rolled back if the PATCH fails.
 */

import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../api.js'
import FilterBar from '../components/FilterBar.jsx'
import { Chip, Priority } from '../components/bits.jsx'
import { BOARD_COLUMNS, STATUS_LABELS, canTransition, useFilters } from '../lib/filters.js'
import { useTickets } from '../lib/useTickets.js'
import { ago } from '../lib/format.js'

function Card({ ticket, blockedBy, onDragStart, onDragEnd, dragging }) {
  const navigate = useNavigate()
  return (
    <div
      className={`card${dragging ? ' dragging' : ''}`}
      draggable
      onDragStart={(event) => onDragStart(event, ticket)}
      onDragEnd={onDragEnd}
      onClick={() => navigate(`/t/${ticket.key}`)}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter') navigate(`/t/${ticket.key}`)
      }}
    >
      <div className="card-top">
        <Priority value={ticket.priority} />
        <span className="key">{ticket.key}</span>
        {ticket.closed_unverified && (
          <span className="badge-unverified" title={ticket.unverified_reason || ''}>
            no evidence
          </span>
        )}
        <span className="spacer" />
        <span className="kbdhint">{ago(ticket.updated_at)}</span>
      </div>
      <div className="card-title">{ticket.title}</div>
      <div className="card-chips">
        {ticket.kind !== 'task' && <Chip kind="kind">{ticket.kind}</Chip>}
        {ticket.cities.map((city) => (
          <Chip key={city.id} kind="city" title={city.name}>
            {city.slug}
          </Chip>
        ))}
        {ticket.components.map((component) => (
          <Chip key={component.id} title={component.name}>
            {component.slug}
          </Chip>
        ))}
        {blockedBy?.length > 0 && (
          <Chip kind="blocked" title={`blocked by ${blockedBy.join(', ')}`}>
            blocked by {blockedBy.length}
          </Chip>
        )}
      </div>
    </div>
  )
}

export default function Board() {
  const { filters, toggle, setValue, clear } = useFilters()
  const { tickets, total, loading, error, blockers, patchLocal, reload } = useTickets(filters)
  const [drag, setDrag] = useState(null)
  const [over, setOver] = useState(null)
  const [moveError, setMoveError] = useState(null)

  const onDragStart = (event, ticket) => {
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', ticket.key)
    setDrag({ id: ticket.id, key: ticket.key, status: ticket.status })
  }

  const onDrop = async (event, status) => {
    event.preventDefault()
    setOver(null)
    const dragged = drag
    setDrag(null)
    if (!dragged || dragged.status === status) return
    if (!canTransition(dragged.status, status)) {
      setMoveError(`${dragged.key}: ${dragged.status} → ${status} is not an allowed transition`)
      return
    }
    setMoveError(null)
    patchLocal(dragged.id, { status })
    try {
      const updated = await api.updateTicket(dragged.key, { status })
      patchLocal(dragged.id, { status: updated.status, closed_at: updated.closed_at })
      reload()
    } catch (err) {
      patchLocal(dragged.id, { status: dragged.status })
      setMoveError(`${dragged.key}: ${err.message}`)
    }
  }

  const columns = BOARD_COLUMNS.filter(
    (status) => filters.status.length === 0 || filters.status.includes(status),
  )

  return (
    <>
      <FilterBar
        filters={filters}
        toggle={toggle}
        setValue={setValue}
        clear={clear}
        count={total}
        showStatus
      />
      {error && <div className="notice error">{error.message}</div>}
      {moveError && <div className="notice error">{moveError}</div>}
      <div className="board">
        {columns.map((status) => {
          const inColumn = tickets.filter((t) => t.status === status)
          const blocked = drag && !canTransition(drag.status, status)
          return (
            <div
              key={status}
              className={`col${over === status && !blocked ? ' drop' : ''}${blocked ? ' nodrop' : ''}`}
              onDragOver={(event) => {
                if (blocked) return
                event.preventDefault()
                event.dataTransfer.dropEffect = 'move'
                setOver(status)
              }}
              onDragLeave={() => setOver((s) => (s === status ? null : s))}
              onDrop={(event) => onDrop(event, status)}
            >
              <div className="col-head">
                {STATUS_LABELS[status]}
                <span className="n">{inColumn.length}</span>
              </div>
              <div className="col-body">
                {inColumn.map((ticket) => (
                  <Card
                    key={ticket.id}
                    ticket={ticket}
                    blockedBy={blockers.get(ticket.id)}
                    dragging={drag?.id === ticket.id}
                    onDragStart={onDragStart}
                    onDragEnd={() => {
                      setDrag(null)
                      setOver(null)
                    }}
                  />
                ))}
                {!loading && inColumn.length === 0 && (
                  <div className="kbdhint" style={{ padding: '4px 2px' }}>
                    empty
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
      <div className="filterbar" style={{ borderTop: '1px solid var(--border)', borderBottom: 0 }}>
        <span className="kbdhint">
          Drag a card between columns to change status. Illegal transitions are dimmed.
        </span>
        <span className="spacer" />
        <Link className="btn sm" to={{ pathname: '/list', search: window.location.search }}>
          Open as list
        </Link>
      </div>
    </>
  )
}
